import { createContext, useContext, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Archive, CalendarDays, ClipboardList, HardDrive, KeyRound, LayoutGrid, LogOut, Menu, Settings, ShieldCheck, Users, X } from "lucide-react";
import { api, ApiError, json } from "./lib/api";
import type { Principal } from "./types";
import { Button, ErrorNotice } from "./components/ui";
import { HomePage } from "./pages/HomePage";
import { ZonesPage } from "./pages/ZonesPage";
import { TasksPage } from "./pages/TasksPage";
import { CalendarPage } from "./pages/CalendarPage";
import { ReportPage } from "./pages/ReportPage";
import { StoragePage } from "./pages/StoragePage";
import { SettingsPage } from "./pages/SettingsPage";

interface AuthValue { principal: Principal | null; loading: boolean; login: (pin: string) => Promise<void>; logout: () => Promise<void>; refresh: () => Promise<void>; }
const AuthContext = createContext<AuthValue | null>(null);
export const useAuth = () => useContext(AuthContext)!;

function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => { setPrincipal(await api<Principal>("/auth/me")); };
  useEffect(() => { refresh().catch(() => setPrincipal(null)).finally(() => setLoading(false)); }, []);
  const login = async (pin: string) => {
    const result = await api<{ principal: { id: string; displayName: string; isOwner: boolean } }>("/auth/admin/pin", json("POST", { pin }));
    setPrincipal({ ...result.principal, role: "admin" });
  };
  const logout = async () => { try { await api("/auth/logout", json("POST")); } finally { setPrincipal(null); } };
  return <AuthContext.Provider value={{ principal, loading, login, logout, refresh }}>{children}</AuthContext.Provider>;
}

function LoginPage() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    if (!/^\d{4}$/.test(pin)) return setError("Ingrese un PIN de cuatro dígitos.");
    setBusy(true);
    try { await login(pin); } catch (cause) { setError(cause instanceof ApiError ? cause.message : "No fue posible iniciar sesión."); } finally { setBusy(false); }
  };
  return <main className="login-shell">
    <section className="login-brand" aria-label="Unidad de Biotecnología en Piscicultura">
      <div className="wordmark wordmark--large"><span>UBP</span><small>Unidad de Biotecnología<br />en Piscicultura</small></div>
      <div className="login-thesis"><p className="eyebrow">Ruta digital</p><h1>Servicios de<br />Conserjería</h1><p>Administración de zonas, actividades y jornadas de servicio.</p></div>
      <p className="institution">Universidad Autónoma de Baja California</p>
    </section>
    <section className="login-panel">
      <form className="login-form" onSubmit={submit}>
        <ShieldCheck className="login-icon" aria-hidden />
        <p className="eyebrow">Acceso administrativo</p>
        <h2>Ingrese su PIN</h2>
        <p>Use el PIN de cuatro dígitos asignado a su cuenta.</p>
        {error && <ErrorNotice message={error} />}
        <label className="pin-field"><span>PIN</span><input autoFocus inputMode="numeric" autoComplete="current-password" pattern="[0-9]{4}" maxLength={4} value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))} aria-label="PIN de cuatro dígitos" /></label>
        <Button type="submit" disabled={busy || pin.length !== 4}>{busy ? "Verificando…" : "Ingresar"}</Button>
        <p className="security-note"><KeyRound aria-hidden /> La sesión permanece activa durante 30 días.</p>
      </form>
    </section>
  </main>;
}

const nav = [
  { to: "/", label: "Inicio", icon: LayoutGrid },
  { to: "/zonas", label: "Zonas y QR", icon: Archive },
  { to: "/tareas", label: "Tareas", icon: ClipboardList },
  { to: "/calendario", label: "Calendario", icon: CalendarDays },
  { to: "/reporte", label: "Reporte diario", icon: ClipboardList },
  { to: "/almacenamiento", label: "Almacenamiento", icon: HardDrive },
  { to: "/configuracion", label: "Configuración", icon: Settings }
];

function Shell() {
  const { principal, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  return <div className="app-shell">
    <aside className={`sidebar ${open ? "sidebar--open" : ""}`}>
      <div className="sidebar-head"><div className="wordmark"><span>UBP</span><small>Servicios de<br />Conserjería</small></div><button className="sidebar-close" onClick={() => setOpen(false)} aria-label="Cerrar menú"><X /></button></div>
      <nav aria-label="Navegación principal">{nav.map(({ to, label, icon: Icon }) => <NavLink key={to} end={to === "/"} to={to}><Icon aria-hidden /><span>{label}</span></NavLink>)}</nav>
      <div className="sidebar-foot">
        {principal?.isOwner && <span className="owner-label"><Users aria-hidden /> Administrador propietario</span>}
        <div className="account"><span className="account-avatar">{principal?.displayName.slice(0, 1).toUpperCase()}</span><div><strong>{principal?.displayName}</strong><small>Administración</small></div><button onClick={logout} aria-label="Cerrar sesión"><LogOut /></button></div>
      </div>
    </aside>
    {open && <button className="sidebar-scrim" aria-label="Cerrar menú" onClick={() => setOpen(false)} />}
    <main className="app-main"><div className="mobile-bar"><button onClick={() => setOpen(true)} aria-label="Abrir menú"><Menu /></button><div className="wordmark"><span>UBP</span><small>Conserjería</small></div></div>
      <div className="page-wrap"><Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/zonas" element={<ZonesPage />} />
        <Route path="/tareas" element={<TasksPage />} />
        <Route path="/calendario" element={<CalendarPage />} />
        <Route path="/reporte" element={<ReportPage />} />
        <Route path="/almacenamiento" element={<StoragePage />} />
        <Route path="/configuracion" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></div>
    </main>
  </div>;
}

function Gate() {
  const { principal, loading } = useAuth();
  if (loading) return <div className="boot"><div className="boot-mark">UBP</div><span>Cargando sistema…</span></div>;
  return principal ? <Shell /> : <LoginPage />;
}

export default function App() { return <AuthProvider><Gate /></AuthProvider>; }
